import { useState } from "react";
import { useDraggable } from "@dnd-kit/core";
import { PRIORITY_COLOR } from "./constants";
import { S, Ic, TagChip, Avatar, ProbBar, Dropdown } from "./ui";

// ═══════════════════════════════════════════════════════════
// KANBAN CARD
// ═══════════════════════════════════════════════════════════
export default function KanbanCard({ opp, onClick, onEdit, onDelete, onArchive, isOverlay }) {
  const [hov, setHov] = useState(false);
  
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: opp.id,
    data: { opp },
  });

  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    opacity: isDragging ? 0.5 : 1,
    cursor: isOverlay ? "grabbing" : "grab",
    zIndex: isDragging ? 999 : "auto",
    // Base styles
    background: "#fff", borderRadius: "10px",
    padding: "10px 12px 10px 15px",
    marginBottom: "7px",
    boxShadow: (hov || isOverlay) ? "0 8px 24px rgba(0,0,0,0.15)" : "0 1px 3px rgba(0,0,0,0.07)",
    border: `1px solid ${(hov || isOverlay) ? "#b8c0d8" : "#ebebf2"}`,
    transition: isDragging ? "none" : "all .15s", // disable transition during drag
    position: "relative", overflow: "hidden",
  };

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      onClick={() => !isDragging && onClick && onClick(opp)}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={style}
    >
      {/* Priority bar */}
      {opp.priority && (
        <div style={{
          position: "absolute", left: 0, top: 8, bottom: 8,
          width: "3px", borderRadius: "0 2px 2px 0",
          background: PRIORITY_COLOR[opp.priority] || "transparent",
        }} />
      )}

      {/* Name row */}
      <div style={{ ...S.row(6), justifyContent: "space-between", marginBottom: "6px" }}>
        <div style={{ fontSize: "13px", fontWeight: "600", color: "#1a1a2e", lineHeight: "1.35", flex: 1 }}>
          {opp.name}
        </div>
        {hov && (
          <div onClick={e => e.stopPropagation()}>
            <Dropdown
              right
              trigger={
                <button style={{
                  width: "20px", height: "20px", border: "none",
                  background: "#f0f0f8", borderRadius: "5px", cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                }}>
                  <Ic n="moreV" s={10} c="#8888a8" />
                </button>
              }
              items={[
                { label: "Edit",    icon: "edit",    onClick: () => onEdit?.(opp)    },
                { label: "Archive", icon: "archive", onClick: () => onArchive?.(opp) },
                "---",
                { label: "Delete",  icon: "trash",   onClick: () => onDelete?.(opp.id), danger: true },
              ]}
            />
          </div>
        )}
      </div>

      {/* Tags */}
      {opp.tags?.length > 0 && (
        <div style={{ ...S.row(3, true), marginBottom: "6px" }}>
          {opp.tags.map((tag, i) => <TagChip key={i} tag={tag} sm />)}
        </div>
      )}

      {/* Company */}
      {opp.company && (
        <div style={{ ...S.row(4), marginBottom: "3px" }}>
          <Ic n="link" s={9} c="#9e9eb8" />
          <span style={{ fontSize: "12px", color: "#0f3460", fontWeight: "500" }}>{opp.company}</span>
        </div>
      )}

      {/* Contact */}
      {opp.contact && (
        <div style={{ ...S.row(4), marginBottom: "6px" }}>
          <Ic n="user" s={9} c="#9e9eb8" />
          <span style={{ fontSize: "12px", color: "#5a5a7a" }}>{opp.contact}</span>
        </div>
      )}

      {/* Amount */}
      {opp.amount != null && (
        <div style={{ fontSize: "13px", fontWeight: "700", color: "#1a1a2e", marginBottom: "6px" }}>
          ${Number(opp.amount).toLocaleString()}
        </div>
      )}

      {/* Probability bar */}
      {opp.probability != null && (
        <div style={{ marginBottom: "8px" }}>
          <ProbBar value={opp.probability} height={3} />
          <span style={{ fontSize: "9.5px", color: "#9e9eb8", fontWeight: "600" }}>
            {opp.probability}% probability
          </span>
        </div>
      )}

      {/* Source chip */}
      {opp.source && (
        <div style={{ marginBottom: "6px" }}>
          <span style={{
            padding: "2px 7px", borderRadius: "10px",
            background: "#f0f0f8", color: "#8888a8",
            fontSize: "10px", fontWeight: "500",
          }}>
            {opp.source}
          </span>
        </div>
      )}

      {/* Footer: dates + avatar */}
      <div style={{ ...S.row(0), justifyContent: "space-between", paddingTop: "7px", borderTop: "1px solid #f2f2f8", alignItems: "flex-end" }}>
        <div style={S.row(6)}>
          {opp.startDate && (
            <div>
              <div style={{ fontSize: "8.5px", color: "#a0a0b8", fontWeight: "700", textTransform: "uppercase", marginBottom: "1px" }}>Start</div>
              <div style={{ fontSize: "10.5px", color: "#5a5a7a", fontWeight: "500" }}>{opp.startDate}</div>
            </div>
          )}
          {opp.startDate && opp.endDate && <Ic n="arrow" s={9} c="#c0c0d8" />}
          {opp.endDate && (
            <div>
              <div style={{ fontSize: "8.5px", color: "#a0a0b8", fontWeight: "700", textTransform: "uppercase", marginBottom: "1px" }}>End</div>
              <div style={{ fontSize: "10.5px", color: "#5a5a7a", fontWeight: "500" }}>{opp.endDate}</div>
            </div>
          )}
        </div>
        <Avatar user={opp.assignee} size={24} />
      </div>

      {/* Activity badge */}
      {opp.activities && (
        <div style={{ ...S.row(4), marginTop: "6px", fontSize: "11px", color: "#0f3460", fontWeight: "500" }}>
          <Ic n="activity" s={10} c="#0f3460" />
          {opp.activities}
        </div>
      )}
    </div>
  );
}
